import { CommonModule, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormGroupDirective,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { isEqual } from 'lodash-es';
import { debounceTime, filter, switchMap } from 'rxjs/operators';

import { InvoiceWorkflowDirective } from '@features/feature-workflow/directives/invoice-workflow.directive';

import { InputComponent } from '@shared/components/input/input.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  EventType,
  PermissionType,
  TrialImplementationStatus,
  WorkflowStep,
} from '@shared/services/gql.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventQuery } from '@shared/store/event/event.query';
import { GlobalStore } from '@shared/store/global/global.store';
import { InvoiceModel } from '@shared/store/invoice/invoice.model';
import { InvoiceService } from '@shared/store/invoice/invoice.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { PurchaseOrdersQuery } from '@shared/store/purchase-orders/purchase-orders.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';

import { InvoiceForm } from './invoice-form.model';
import { InvoiceFormUtils } from './invoice-form.utils';
import { InvoiceNotesComponent } from './invoice-notes.component';
import { invoiceDisabledTooltip } from '../invoices.component';
import { InvoiceValidators } from './services/invoice.validators';

const initialFormValues: InvoiceForm = {
  accrual_period: '',
  discount_total: '',
  discount_total_trial_currency: '',
  due_date: '',
  investigator_total: '',
  investigator_total_trial_currency: '',
  invoice_date: '',
  invoice_no: '',
  invoice_total: '',
  invoice_total_trial_currency: '',
  notes: '',
  pass_thru_total: '',
  pass_thru_total_trial_currency: '',
  selectedPOReference: '',
  services_period: '',
  services_total: '',
  services_total_trial_currency: '',
  vendor_id: '',
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [InvoiceWorkflowDirective],
  imports: [
    InputComponent,
    NgSelectModule,
    ReactiveFormsModule,
    TooltipDirective,
    NgClass,
    FormsModule,
    InvoiceNotesComponent,
    FormErrorDirective,
    CommonModule,
  ],
  selector: 'aux-invoice-form',
  templateUrl: 'invoice-form.component.html',
})
export class InvoiceFormComponent {
  authQuery = inject(AuthQuery);
  authService = inject(AuthService);
  eventQuery = inject(EventQuery);
  formBuilder = inject(NonNullableFormBuilder);
  globalStore = inject(GlobalStore);
  invoiceCtx = inject(InvoiceWorkflowDirective);
  invoiceService = inject(InvoiceService);
  mainQuery = inject(MainQuery);
  orgQuery = inject(OrganizationQuery);
  purchaseOrdersQuery = inject(PurchaseOrdersQuery);
  workflowQuery = inject(WorkflowQuery);

  private cdr = inject(ChangeDetectorRef);

  formValues = signal(initialFormValues);
  initializing = signal(true);

  accrualMinDate = computed(() => {
    const { currentOpenMonth } = this.mainQuery.mainState();

    return dayjs(currentOpenMonth).format('YYYY-MM');
  });

  invoice = input.required<InvoiceModel>();

  filteredPONumbers = computed(() => {
    const invoice = this.invoice();
    const { vendor_id } = this.formValues();
    const purchaseOrders = this.purchaseOrdersQuery.$selectAll();

    if (vendor_id) {
      return purchaseOrders.filter((po) => po.organization_id === vendor_id);
    }

    return purchaseOrders.filter((po) => po.id === invoice.organization.id);
  });
  isAccrualPeriodInClosedMonth = computed(() => {
    const trial = this.mainQuery.selectedTrial();
    const { currentOpenMonth } = this.mainQuery.mainState();
    const invoice = this.invoice();

    return InvoiceFormUtils.isAccrualPeriodInClosedMonth({ currentOpenMonth, invoice, trial });
  });
  isDecline = computed(() => this.invoice().invoice_status === 'STATUS_DECLINED');

  hasEditPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPDATE_INVOICES],
  });
  iCloseMonthsProcessing = this.eventQuery.selectProcessingEvent(EventType.CLOSE_TRIAL_MONTH);
  isInvoiceFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_INVOICES
  );

  isFormDisabled = computed(() => {
    const isAdmin = this.authQuery.adminUser();

    return (
      (!isAdmin && !this.hasEditPermission()) ||
      this.isInvoiceFinalized() ||
      !!this.iCloseMonthsProcessing()
    );
  });
  isJEConfigSetToAttribute = computed(() => {
    return !!this.globalStore.primaryAttribute.attribute_name();
  });
  maxDate = computed(() => {
    const { trialEndDate } = this.mainQuery.mainState();

    return dayjs(trialEndDate).format('YYYY-MM');
  });
  vendorIdTooltip = computed(() =>
    this.invoice().has_prepaid ? MessagesConstants.PREPAIDS.DISABLED_VENDOR : ''
  );

  invoiceFormRef = viewChild.required<FormGroupDirective>('invoiceFormRef');

  accrualValidators: ValidatorFn[] = [
    (control) => {
      if (!this.submit) {
        return null;
      }

      if (this.isFormDisabled() || this.isAccrualPeriodInClosedMonth()) {
        return null;
      }

      const value = control.value;
      const { currentOpenMonth } = this.mainQuery.mainState();
      const trial = this.mainQuery.selectedTrial();
      const accrualDate = dayjs(`${value}-01`);

      if (
        value &&
        accrualDate.isBefore(currentOpenMonth, 'month') &&
        (trial?.implementation_status ===
          TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED ||
          trial?.implementation_status === TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE)
      ) {
        return { before: true };
      }

      return null;
    },
  ];
  customErrorMessages = {
    before: '',
  };
  customErrorServicesPeriodMessages = {
    before: '',
    outTimeline: '',
  };
  disableFormEffect = effect(() => {
    if (this.isFormDisabled()) {
      this.invoiceForm.disable();
    } else {
      this.invoiceForm.enable();

      if (this.isAccrualPeriodInClosedMonth()) {
        [
          'selectedPOReference',
          'due_date',
          'notes',
          'accrual_period',
          'vendor_id',
          'invoice_no',
          'invoice_date',
          'invoice_total',
          'investigator_total',
          'services_total',
          'discount_total',
          'pass_thru_total',
          'invoice_total_trial_currency',
          'pass_thru_total_trial_currency',
          'services_total_trial_currency',
          'discount_total_trial_currency',
          'investigator_total_trial_currency',
          // 'services_period' is intentionally omitted so it remains enabled
        ].forEach((controlName) => {
          this.invoiceForm.get(controlName)?.disable();
        });
        // Explicitly enable 'services_period' in case it was previously disabled
        this.invoiceForm.get('services_period')?.enable();
      } else {
        [
          'selectedPOReference',
          'due_date',
          'notes',
          'accrual_period',
          'vendor_id',
          'invoice_no',
          'invoice_date',
          'invoice_total',
          'investigator_total',
          'services_total',
          'discount_total',
          'pass_thru_total',
          'invoice_total_trial_currency',
          'pass_thru_total_trial_currency',
          'services_total_trial_currency',
          'discount_total_trial_currency',
          'investigator_total_trial_currency',
          'services_period',
        ].forEach((controlName) => {
          this.invoiceForm.get(controlName)?.enable();
        });
      }

      if (this.invoice().has_prepaid) {
        this.invoiceForm.controls.vendor_id.disable();
      }

      if (this.isJEConfigSetToAttribute()) {
        this.invoiceForm.controls.invoice_total.disable();
      }
    }
  });
  invoiceForm = this.formBuilder.group(initialFormValues);
  invoiceLockTooltip = this.invoiceCtx.invoiceLockTooltip;
  protected readonly messagesConstants = MessagesConstants;

  // needed for checking if the form values are changed or not.
  private initialFormValues = initialFormValues;

  patchForm = effect(() => {
    this.initializing.set(true);
    const invoice = this.invoice();

    this.invoiceForm.setValue(InvoiceFormUtils.invoiceModelToForm(invoice), { emitEvent: false });

    this.initialFormValues = this.invoiceForm.getRawValue();
    this.formValues.set(this.invoiceForm.getRawValue());
    this.invoiceForm.markAsPristine();
    this.initializing.set(false);
  });
  servicesPeriodValidators: ValidatorFn[] = [
    (control) => {
      if (!this.submit) {
        return null;
      }

      if (this.isFormDisabled()) {
        return null;
      }

      const value = control.value;

      const { trialEndDate, trialStartDate } = this.mainQuery.getValue();

      const isServicesPeriodOutsideTrialTimeline = InvoiceValidators.validateServicesPeriod(
        value,
        trialStartDate,
        trialEndDate
      );

      if (value && isServicesPeriodOutsideTrialTimeline) {
        return { outTimeline: true };
      }

      return null;
    },
  ];
  submit = false;

  // on initialize or when invoice changes, we are updating the form values.
  // While we are updating the form values we shouldn't get any event in the valueChanges subscription.
  // in order to fix that we are using initializing signal.
  private initializing$ = toObservable(this.initializing);

  cancelNotes() {
    const invoice = this.invoice();

    this.invoiceForm.controls.notes.setValue(invoice.notes?.[0]?.message || '');
    this.invoiceForm.controls.notes.markAsPristine();
  }

  canDeactivate() {
    return isEqual(this.invoiceForm.getRawValue(), this.initialFormValues);
  }

  constructor() {
    this.initializing$
      .pipe(
        takeUntilDestroyed(),
        filter((value) => !value),
        switchMap(() => this.invoiceForm.valueChanges),
        debounceTime(100)
      )
      .subscribe(() => {
        const next = this.invoiceForm.getRawValue();

        this.formValues.set(next);
      });
  }

  formTooltip(field = '') {
    if (!this.hasEditPermission()) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    if (this.invoiceCtx.isInvoiceLocked()) {
      return this.invoiceCtx.invoiceLockTooltip();
    }

    if (this.isAccrualPeriodInClosedMonth() && field !== 'services_period') {
      return invoiceDisabledTooltip;
    }

    return '';
  }

  onVendorChange() {
    const control = this.invoiceForm.get('selectedPOReference');

    if (control) {
      (control as AbstractControl<string | undefined>).setValue(undefined);
    }
  }

  refreshView() {
    this.cdr.detectChanges();
  }

  async saveNotes() {
    const invoice = this.invoice();
    const { notes } = this.formValues();

    if (invoice.notes?.length) {
      await this.invoiceService.updateInvoiceNote(invoice, notes);
    } else {
      await this.invoiceService.createInvoiceNote(invoice, notes);
    }

    this.invoiceForm.controls.notes.markAsPristine();
  }
}
